import { useEffect, useMemo } from 'react';
import { useGetConsistencyCollectionsQuery } from 'types/dt-graphql-apollo-v2';
import {
  ConsistencyReportSummary,
  ConsistencyRuleRunResult,
  aggregateRuns,
  deriveWorstStatus,
  isRuleDisabled,
  latestRunStatus,
  toRuleOperator,
  toRuleSeverity,
  mapConditionChecks,
} from '../consistencyReportTypes';

const POLL_INTERVAL_MS = 5000;

export type ConsistencyReportDetail = ConsistencyReportSummary & {
  ruleResults: ConsistencyRuleRunResult[];
};

type CollectionItems = NonNullable<
  NonNullable<
    ReturnType<typeof useGetConsistencyCollectionsQuery>['data']
  >['listConsistencyCollection']
>['items'];

type Collection = NonNullable<CollectionItems[number]>;
type Rule = NonNullable<NonNullable<Collection['rules']>['items'][number]>;

// Locate the collection that contains a rule whose run history includes the
// given reportId. Needed because the URL carries a reportId but the details
// page shows the full current state of the collection (latest run per rule).
// This deliberately matches across disabled rules too — it is only a locator;
// the disabled rules are dropped later when building rulesWithLatest.
const findCollectionForReport = (
  collections: CollectionItems,
  reportId: string
): Collection | null => {
  for (const collection of collections) {
    if (!collection?.rules?.items) continue;
    for (const rule of collection.rules.items) {
      if (!rule?.runStatuses?.items) continue;
      if (rule.runStatuses.items.some((r) => r?.reportId === reportId)) {
        return collection;
      }
    }
  }
  return null;
};

export const useConsistencyReport = (
  reportId: string,
  digitalTwinId: string
) => {
  // `cache-and-network` ensures a stale cached value (e.g. from a single-rule
  // run kicked off elsewhere) is refreshed on mount.
  const { data, loading, error, startPolling, stopPolling } =
    useGetConsistencyCollectionsQuery({
      variables: { projectId: digitalTwinId },
      skip: !reportId || !digitalTwinId,
      fetchPolicy: 'cache-and-network',
    });

  const report = useMemo((): ConsistencyReportDetail | null => {
    const collections = data?.listConsistencyCollection?.items ?? [];
    const collection = findCollectionForReport(collections, reportId);
    if (!collection?.rules?.items) return null;

    const rulesWithLatest = collection.rules.items.flatMap((rule) => {
      if (!rule || isRuleDisabled(rule.tags)) return [];
      const latest = latestRunStatus(rule.runStatuses?.items);
      return latest ? [{ rule, latest }] : [];
    });
    if (rulesWithLatest.length === 0) return null;

    const { statuses, totalValid, totalFailed, latestRunAt } = aggregateRuns(
      rulesWithLatest.map((r) => r.latest)
    );

    const ruleResults: ConsistencyRuleRunResult[] = rulesWithLatest.map(
      ({ rule, latest }) => ({
        runStatusId: latest.externalId,
        status: deriveWorstStatus([latest.status ?? 'IN_PROGRESS']),
        ruleId: rule.externalId,
        ruleName: rule.name,
        customerDefinedId: rule.customerDefinedId ?? null,
        operator: toRuleOperator(rule.operator),
        sourceSchemaFilterName: rule.sourceSchemaFilter?.name ?? '',
        sourceAttribute: rule.sourceAttribute,
        targetSchemaFilterName: rule.targetSchemaFilter?.name ?? '',
        targetAttribute: rule.targetAttribute,
        severity: toRuleSeverity(rule.severity),
        errorMessage: rule.errorMessage ?? null,
        sourceFilters: rule.sourceFilters ?? null,
        targetFilters: rule.targetFilters ?? null,
        conditionChecks: mapConditionChecks(rule.conditionChecks?.items),
        totalValid: Number(latest.totalValid ?? 0),
        totalFailed: Number(latest.totalFailed ?? 0),
      })
    );

    const schemaSet = new Set<string>();
    for (const sf of collection.schemaFilters?.items ?? []) {
      if (sf?.schemaType) schemaSet.add(sf.schemaType);
    }

    return {
      reportId,
      collectionId: collection.externalId,
      collectionName: collection.name,
      status: deriveWorstStatus(statuses),
      runAt: latestRunAt,
      schemas: Array.from(schemaSet),
      rulesCount: ruleResults.length,
      totalValid,
      totalFailed,
      ruleResults,
    };
  }, [data, reportId]);

  // Poll while a run is IN_PROGRESS so the page auto-refreshes as the job
  // completes. Using Apollo's imperative start/stop API (side effect) rather
  // than driving a pollInterval option through useState.
  const isInProgress = report?.status === 'IN_PROGRESS';
  useEffect(() => {
    if (isInProgress) startPolling(POLL_INTERVAL_MS);
    else stopPolling();
  }, [isInProgress, startPolling, stopPolling]);

  return { report, loading, error };
};
